import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  inject,
  OnDestroy,
  Output,
} from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { NotificationPosition, NotificationTheme } from '../models/notification.enum';
import { NOTIFICATION_POSITION, NotificationInstance } from '../models/notification.model';
import {
  NotificationDismissEvent,
  NotificationToastComponent,
} from '../notification-toast/notification-toast.component';

const TOP_POSITIONS: ReadonlyArray<NotificationPosition> = [
  NotificationPosition.TopLeft,
  NotificationPosition.TopCenter,
  NotificationPosition.TopRight,
];

/**
 * Holds the stack of toasts anchored at one screen position. One instance is
 * attached to a dedicated CDK overlay per position by `NotificationService`.
 *
 * Deliberately has no dependency on the service: it is driven entirely through
 * {@link setNotifications} and reports back through its outputs, which keeps
 * the two files free of a circular import.
 */
@Component({
    selector: 'du-notification-container',
    imports: [CommonModule, NotificationToastComponent],
    templateUrl: './notification-container.component.html',
    styleUrls: ['./notification-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationContainerComponent implements OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);

  readonly position = inject(NOTIFICATION_POSITION);

  private readonly notifications$ = new BehaviorSubject<NotificationInstance[]>([]);

  /**
   * Ordered so the newest toast always sits closest to the anchored edge:
   * top-down for the top row, bottom-up for the bottom row.
   */
  readonly toasts$: Observable<NotificationInstance[]> = this.notifications$.pipe(
    map((list) => (this.isTopAnchored ? [...list].reverse() : list))
  );

  /** Accessible name of the stack's landmark region. */
  ariaLabel = 'Notifications';

  /** Accessible name handed to every toast's × button. */
  closeAriaLabel = 'Dismiss notification';

  @Output() readonly dismissed = new EventEmitter<NotificationDismissEvent>();
  @Output() readonly actionInvoked = new EventEmitter<NotificationInstance>();

  private currentTheme = NotificationTheme.Auto;

  @HostBinding('class')
  get hostClasses(): string {
    return [
      'du-toast-stack',
      `du-toast-stack--${this.position}`,
      `du-toast-theme--${this.currentTheme}`,
    ].join(' ');
  }

  @HostBinding('attr.role')
  readonly roleAttr = 'region';

  @HostBinding('attr.aria-label')
  get ariaLabelAttr(): string {
    return this.ariaLabel;
  }

  ngOnDestroy(): void {
    this.notifications$.complete();
    this.dismissed.complete();
    this.actionInvoked.complete();
  }

  /** Replaces the stack contents; the `async` pipe handles change detection. */
  setNotifications(notifications: NotificationInstance[]): void {
    this.notifications$.next(notifications);
  }

  setTheme(theme: NotificationTheme): void {
    if (theme === this.currentTheme) {
      return;
    }
    this.currentTheme = theme;
    // Host bindings are evaluated by the host view, which the `async` pipe does
    // not mark on its own — nudge it explicitly.
    this.cdr.markForCheck();
  }

  trackById(_index: number, toast: NotificationInstance): string {
    return toast.id;
  }

  private get isTopAnchored(): boolean {
    return TOP_POSITIONS.indexOf(this.position) !== -1;
  }
}
